import { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';

/**
 * Hook to fetch and manage OE scores from Hugo_OESnapshots.
 * Listens in real-time for snapshot changes.
 */
export function useOEScores(organizationId, scope) {
  const [snapshots, setSnapshots] = useState([]);
  const [latestScores, setLatestScores] = useState(null);
  const [elementBreakdown, setElementBreakdown] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!organizationId) {
      setSnapshots([]);
      setLatestScores(null);
      setElementBreakdown(null);
      setWarnings([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const constraints = [where('organizationId', '==', organizationId)];

    // Always pin the scope type: the org-level view must NOT mix in site/dept/team
    // snapshots (otherwise "latest" can be a team's scores shown as the whole org's).
    if (scope && scope.type && scope.type !== 'organization') {
      constraints.push(where('scope.type', '==', scope.type));
      constraints.push(where('scope.id', '==', scope.id));
    } else {
      constraints.push(where('scope.type', '==', 'organization'));
    }
    constraints.push(orderBy('calculatedAt', 'desc'));

    const q = query(collection(db, 'Hugo_OESnapshots'), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const snapshotData = [];
        querySnapshot.forEach((doc) => {
          snapshotData.push({ id: doc.id, ...doc.data() });
        });

        setSnapshots(snapshotData);

        // Latest snapshot is the first one (ordered desc)
        if (snapshotData.length > 0) {
          const latest = snapshotData[0];
          setLatestScores(latest.oeScores || null);
          setElementBreakdown(latest.elementScores || null);
          setWarnings(latest.warnings || []);
        } else {
          setLatestScores(null);
          setElementBreakdown(null);
          setWarnings([]);
        }

        setLoading(false);
      },
      (err) => {
        console.error('Error fetching OE snapshots:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [organizationId, scope?.type, scope?.id]);

  return { snapshots, latestScores, elementBreakdown, warnings, loading, error };
}

export default useOEScores;
